import React from "react";
import { useTheme } from "@/theme";
import {
  Database,
  Network,
  Layers,
  Server,
  Brain,
  TrendingUp,
  Wand2,
} from "lucide-react";

const TECHNIQUES = [
  { label: "DB Lock contention", target: "db", out: "rca", engine: "RULE · oracle.adv-lock", color: "#F59E0B" },
  { label: "Memory leak forecast", target: "srv", out: "predict", engine: "ML · isolation-forest", color: "#00D4FF" },
  { label: "TLS handshake regression", target: "net", out: "rca", engine: "STAT · prophet", color: "#00D4FF" },
  { label: "JVM GC pressure", target: "mw", out: "optimize", engine: "ML · classifier-v3", color: "#00D4FF" },
  { label: "Disk pressure (90d)", target: "srv", out: "predict", engine: "STAT · prophet", color: "#F59E0B" },
  { label: "Slow query rewrite", target: "db", out: "optimize", engine: "ML · randomforest", color: "#F59E0B" },
];

const SOURCES = [
  { id: "db", Icon: Database, k: "DATABASE", sub: "Oracle · MSSQL · PG" },
  { id: "srv", Icon: Server, k: "SERVER", sub: "Linux · Windows" },
  { id: "net", Icon: Network, k: "NETWORK", sub: "DNS · SSL · LB" },
  { id: "mw", Icon: Layers, k: "MIDDLEWARE", sub: "WebLogic · Tomcat" },
];

const OUTCOMES = [
  { id: "predict", Icon: TrendingUp, k: "PREDICT", sub: "5min → 90d", accent: "#F59E0B" },
  { id: "rca", Icon: Brain, k: "RCA REPORT", sub: "91% accuracy", accent: "#00D4FF" },
  { id: "optimize", Icon: Wand2, k: "OPTIMIZE", sub: "auto-remediate", accent: "#00D4FF" },
];

const ENGINES_TICKER = [
  { t: "ML", k: "ML.classifier-v3", color: "#00D4FF" },
  { t: "RULE", k: "rule.oracle.adv-lock", color: "#F59E0B" },
  { t: "STAT", k: "stat.prophet", color: "#00D4FF" },
  { t: "ML", k: "ml.isolation-forest", color: "#00D4FF" },
  { t: "LLM", k: "llm.llama-3.1", color: "#F59E0B" },
];

const VB_W = 820;
const VB_H = 640;
const HUB_X = 410;
const HUB_Y = 300;

const sourceCoords = {
  db: { x: 168, y: 148 },
  srv: { x: 168, y: 258 },
  net: { x: 168, y: 368 },
  mw: { x: 168, y: 478 },
};

const outcomeCoords = {
  predict: { x: 652, y: 168 },
  rca: { x: 652, y: 300 },
  optimize: { x: 652, y: 432 },
};

function NodeLabel({ label, sub, active, color, palette }) {
  return (
    <div
      className="px-2.5 py-1.5 rounded-lg border-2 text-left shrink-0 w-[118px]"
      style={{
        background: palette.chip,
        borderColor: active ? `${color}88` : palette.lineSoft,
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
      }}
    >
      <div
        className="font-mono text-[10px] font-bold tracking-widest2 leading-tight"
        style={{ color: active ? color : palette.ink }}
      >
        {label}
      </div>
      <div className="font-mono text-[9px] mt-0.5 font-semibold leading-tight" style={{ color: palette.dim }}>
        {sub}
      </div>
    </div>
  );
}

function Node({ x, y, label, sub, Icon, active, color = "#00D4FF", anchor = "left", palette }) {
  const iconHalf = 24;

  return (
    <div
      className="absolute flex items-center gap-2 z-20"
      style={{
        left: `${(x / VB_W) * 100}%`,
        top: `${(y / VB_H) * 100}%`,
        transform: `translate(-${iconHalf}px, -50%)`,
      }}
    >
      {anchor === "left" && (
        <NodeLabel label={label} sub={sub} active={active} color={color} palette={palette} />
      )}
      <div className="relative shrink-0">
        <div
          className={`absolute inset-0 rounded-xl blur-md transition-opacity duration-500 ${
            active ? "opacity-90" : "opacity-25"
          }`}
          style={{ background: `radial-gradient(circle, ${color}66, transparent 70%)` }}
        />
        <div
          className="relative w-12 h-12 rounded-xl border-2 flex items-center justify-center transition-all duration-300"
          style={{
            borderColor: active ? color : palette.line,
            background: active ? `${color}22` : palette.chip,
            boxShadow: active
              ? `0 0 20px ${color}55, 0 3px 10px rgba(15, 23, 42, 0.12)`
              : "0 2px 8px rgba(15, 23, 42, 0.1)",
          }}
        >
          <Icon size={20} color={active ? color : palette.muted} strokeWidth={2.25} />
          {active && (
            <span
              className="absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse-glow border border-white/80"
              style={{ background: color }}
            />
          )}
        </div>
      </div>
      {anchor === "right" && (
        <NodeLabel label={label} sub={sub} active={active} color={color} palette={palette} />
      )}
    </div>
  );
}

export default function LiveEngine({ className = "" }) {
  const { theme } = useTheme();
  const palette =
    theme === "light"
      ? {
          bg: "#FFFFFF",
          bgSoft: "#F1F3F8",
          line: "#94A3B8",
          lineSoft: "#CBD5E1",
          muted: "#334155",
          dim: "#475569",
          ink: "#0F172A",
          chip: "#FFFFFF",
        }
      : {
          bg: "#121928",
          bgSoft: "#0E121C",
          line: "#4A5568",
          lineSoft: "#2D3748",
          muted: "#CBD5E1",
          dim: "#94A3B8",
          ink: "#F8FAFC",
          chip: "#161C2A",
        };

  const [step, setStep] = React.useState(0);
  React.useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % TECHNIQUES.length), 2400);
    return () => clearInterval(id);
  }, []);

  const current = TECHNIQUES[step];
  const activeEngineIdx = step % ENGINES_TICKER.length;
  const activeEngine = ENGINES_TICKER[activeEngineIdx];
  const src = sourceCoords[current.target];
  const dst = outcomeCoords[current.out];
  const pathD = `M ${src.x},${src.y} L ${HUB_X},${HUB_Y} L ${dst.x},${dst.y}`;

  return (
    <div
      className={`relative w-full overflow-hidden grid-bg noise ${className}`}
      style={{ backgroundColor: `${palette.bgSoft}cc`, aspectRatio: `${VB_W} / ${VB_H}` }}
      data-testid="live-engine"
    >
      {["tl", "tr", "bl", "br"].map((p) => (
        <span
          key={p}
          className={`absolute w-4 h-4 z-30 ${
            p === "tl"
              ? "top-2 left-2 border-t-2 border-l-2"
              : p === "tr"
                ? "top-2 right-2 border-t-2 border-r-2"
                : p === "bl"
                  ? "bottom-2 left-2 border-b-2 border-l-2"
                  : "bottom-2 right-2 border-b-2 border-r-2"
          }`}
          style={{ borderColor: "#00D4FF" }}
        />
      ))}

      {/* TOP STATUS BAR */}
      <div className="absolute top-2.5 left-3 right-3 z-30">
        <div
          className="flex flex-wrap items-center gap-2 px-3 py-1.5 border-2 font-mono text-[10px] tracking-widest2 rounded-lg"
          style={{ borderColor: "#F59E0B88", background: palette.chip, boxShadow: "0 2px 8px rgba(15,23,42,0.08)" }}
        >
          <span className="w-2 h-2 rounded-full bg-amber animate-pulse-glow shrink-0" />
          <span className="text-amber font-semibold">LIVE</span>
          <span className="text-ink font-semibold hidden sm:inline">AI ENGINE ORCHESTRATION</span>
          <span className="text-line hidden md:inline">·</span>
          <span className="text-ink-mute hidden md:inline">
            risk <span className="text-amber font-bold">CRITICAL</span>
          </span>
          <span className="text-line hidden lg:inline">·</span>
          <span className="text-ink-mute hidden lg:inline ml-auto">
            confidence <span className="text-cyan font-bold">94.3%</span>
          </span>
        </div>
      </div>

      {/* Diagram layer — inset from chrome zones */}
      <div className="absolute inset-x-0 top-[52px] bottom-[88px]">
        <svg viewBox={`0 0 ${VB_W} ${VB_H}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
          <defs>
            <radialGradient id="hubGlowR" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.55" />
              <stop offset="60%" stopColor="#00D4FF" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#00D4FF" stopOpacity="0" />
            </radialGradient>
            <filter id="softBlur" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.5" />
            </filter>
          </defs>

          {SOURCES.map((s, i) => {
            const sc = sourceCoords[s.id];
            const color = i % 2 === 0 ? "#00D4FF" : "#F59E0B";
            const isActive = current.target === s.id;
            return (
              <g key={`src-${s.id}`}>
                <line x1={sc.x} y1={sc.y} x2={HUB_X} y2={HUB_Y} stroke={palette.line} strokeWidth="1.5" strokeOpacity="0.72" />
                <line
                  x1={sc.x}
                  y1={sc.y}
                  x2={HUB_X}
                  y2={HUB_Y}
                  stroke={color}
                  strokeOpacity={isActive ? 0.95 : 0.58}
                  strokeWidth={isActive ? 1.6 : 1.15}
                  strokeDasharray="4 6"
                  className="stroke-flow"
                  style={{ animationDelay: `${i * 0.3}s` }}
                />
              </g>
            );
          })}

          {OUTCOMES.map((o, i) => {
            const oc = outcomeCoords[o.id];
            const isActive = current.out === o.id;
            return (
              <g key={`out-${o.id}`}>
                <line x1={HUB_X} y1={HUB_Y} x2={oc.x} y2={oc.y} stroke={palette.line} strokeWidth="1.5" strokeOpacity="0.72" />
                <line
                  x1={HUB_X}
                  y1={HUB_Y}
                  x2={oc.x}
                  y2={oc.y}
                  stroke={o.accent}
                  strokeOpacity={isActive ? 0.95 : 0.58}
                  strokeWidth={isActive ? 1.6 : 1.15}
                  strokeDasharray="4 6"
                  className="stroke-flow"
                  style={{ animationDelay: `${i * 0.4 + 0.3}s` }}
                />
              </g>
            );
          })}

          <path d={pathD} fill="none" stroke={current.color} strokeWidth="2" strokeOpacity="0.35" filter="url(#softBlur)" />
          <path key={`active-${step}`} id="active-path" d={pathD} fill="none" stroke={current.color} strokeWidth="1.6" strokeOpacity="0.9" />

          <circle cx={HUB_X} cy={HUB_Y} r="95" fill="url(#hubGlowR)" />
          <circle cx={HUB_X} cy={HUB_Y} r="68" fill="none" stroke="#00D4FF" strokeOpacity="0.18" strokeDasharray="2 5">
            <animateTransform attributeName="transform" type="rotate" from={`0 ${HUB_X} ${HUB_Y}`} to={`360 ${HUB_X} ${HUB_Y}`} dur="22s" repeatCount="indefinite" />
          </circle>

          <circle cx={HUB_X} cy={HUB_Y} r="52" fill={palette.chip} stroke="#00D4FF" strokeWidth="2.5" />
          <text x={HUB_X} y={HUB_Y - 4} textAnchor="middle" fill={palette.ink} fontFamily="Outfit, sans-serif" fontWeight="700" fontSize="15" letterSpacing="1">
            AI HUB
          </text>
          <text x={HUB_X} y={HUB_Y + 14} textAnchor="middle" fill="#F59E0B" fontFamily="JetBrains Mono, monospace" fontSize="10" fontWeight="600" letterSpacing="1.8">
            24 ENGINES
          </text>

          <circle r="4" fill={current.color}>
            <animateMotion key={`m-${step}`} dur="1.8s" repeatCount="indefinite" rotate="auto">
              <mpath xlinkHref="#active-path" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;0" dur="1.8s" repeatCount="indefinite" />
          </circle>
        </svg>

        {SOURCES.map((s) => (
          <Node
            key={s.id}
            palette={palette}
            x={sourceCoords[s.id].x}
            y={sourceCoords[s.id].y}
            Icon={s.Icon}
            label={s.k}
            sub={s.sub}
            color={current.target === s.id ? current.color : "#00D4FF"}
            active={current.target === s.id}
            anchor="left"
          />
        ))}

        {OUTCOMES.map((o) => (
          <Node
            key={o.id}
            palette={palette}
            x={outcomeCoords[o.id].x}
            y={outcomeCoords[o.id].y}
            Icon={o.Icon}
            label={o.k}
            sub={o.sub}
            color={current.out === o.id ? current.color : o.accent}
            active={current.out === o.id}
            anchor="right"
          />
        ))}
      </div>

      {/* Active engine chip — single row, no overlap */}
      <div className="absolute left-3 right-3 bottom-[46px] z-30 flex justify-center">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 border-2 rounded-lg max-w-full"
          style={{
            borderColor: `${activeEngine.color}99`,
            background: palette.chip,
            boxShadow: `0 0 16px ${activeEngine.color}33`,
          }}
        >
          <span className="w-2 h-2 rounded-full animate-pulse-glow shrink-0" style={{ background: activeEngine.color }} />
          <span className="font-mono text-[10px] font-bold tracking-widest2 text-ink">{activeEngine.t}</span>
          <span className="font-mono text-[10px] font-semibold text-ink-mute truncate">{activeEngine.k}</span>
          <span className="font-mono text-[10px] font-bold text-amber shrink-0">· triggered</span>
        </div>
      </div>

      {/* Bottom stage bar */}
      <div
        className="absolute left-3 right-3 bottom-2.5 px-3 py-1.5 flex items-center justify-between gap-3 font-mono text-[10px] tracking-widest2 rounded-lg border-2 z-30 min-h-[36px]"
        style={{ borderColor: `${current.color}66`, background: palette.chip, boxShadow: "0 2px 10px rgba(15,23,42,0.08)" }}
      >
        <div className="text-ink-mute truncate min-w-0">
          <span className="text-ink font-bold">STAGE </span>
          <span className="text-ink font-bold">{String(step + 1).padStart(2, "0")}</span>
          <span className="text-line"> / {TECHNIQUES.length}</span>
          <span className="mx-2 text-line hidden sm:inline">·</span>
          <span className="text-ink font-semibold hidden sm:inline">{current.label}</span>
        </div>
        <div className="text-ink-mute shrink-0">
          <span className="text-ink font-bold">ENGINE </span>
          <span className="font-bold" style={{ color: current.color }}>
            {current.engine}
          </span>
        </div>
      </div>
    </div>
  );
}
